<ng-template
  #dateTemplate
  let-question
  let-prefix="prefix"
  let-formGroup="formGroup"
>
  @let dateQ = _asDate(question);
  <ng-container [formGroup]="formGroup">
    @switch (dateQ.options.type) {
      @case ('year') {
        <myre-year-picker
          [id]="prefix + dateQ.key"
          [error]="hasError(dateQ)"
          [warning]="hasWarning(dateQ)"
          [min]="dateQ.options.min"
          [max]="dateQ.options.max"
          [triggerIcon]="dateQ.options.triggerIcon"
          [dateFilter]="dateQ.options.dateFilter"
          [startAt]="dateQ.options.startAt"
          [readonly]="dateQ.readonly"
          [placeholder]="dateQ.placeholder"
          [formControlName]="dateQ.key"
        />
      }

      @case ('month') {
        <myre-month-picker
          [id]="prefix + dateQ.key"
          [error]="hasError(dateQ)"
          [warning]="hasWarning(dateQ)"
          [min]="dateQ.options.min"
          [max]="dateQ.options.max"
          [triggerIcon]="dateQ.options.triggerIcon"
          [dateFilter]="dateQ.options.dateFilter"
          [startAt]="dateQ.options.startAt"
          [readonly]="dateQ.readonly"
          [placeholder]="dateQ.placeholder"
          [formControlName]="dateQ.key"
        />
      }

      @default {
        <myre-date-picker
          [id]="prefix + dateQ.key"
          [error]="hasError(dateQ)"
          [warning]="hasWarning(dateQ)"
          [min]="dateQ.options.min"
          [max]="dateQ.options.max"
          [triggerIcon]="dateQ.options.triggerIcon"
          [dateFilter]="dateQ.options.dateFilter"
          [startAt]="dateQ.options.startAt"
          [readonly]="dateQ.readonly"
          [placeholder]="dateQ.placeholder"
          [formControlName]="dateQ.key"
        />
      }
    }
  </ng-container>
</ng-template>

<div
  class="question"
  [class.question--flex-one]="!!question.actionButton"
  [attr.data-testid]="question.key"
>
  <span
    class="element {{ question.elementType }}"
    [formGroup]="formGroup"
    [ngClass]="{
      error: !isQuestionGroup(question) && hasError(),
      warning: !isQuestionGroup(question) && hasWarning(),
      empty: control?.value === null || control?.value === undefined,
      readonly: question.readonly,
    }"
  >
    <span class="body">
      @if (hasUpperLabel(question)) {
        <div class="upper-labels-container">
          <div
            (click)="question.labelClick()"
            class="upper-labels {{
              'position-' + question.headerContentPosition
            }}"
          >
            @if (question.label) {
              <label
                [for]="prefix + question.key"
                [class.first-line]="firstLine"
                [class.ellipsis]="question.ellipsis"
                [class.center]="question.center"
                class="myre-form__label"
              >
                <span
                  >{{ question.label | translate }}
                  @if (question.click) {
                    <span>
                      <span>&nbsp;</span>

                      <a class="link" (click)="question.click($event)">{{
                        question.link | translate
                      }}</a>
                    </span>
                  }

                  @if (question.required) {
                    <span>&nbsp;*</span>
                  }
                </span>
              </label>
            }

            @if (question.secondLabel) {
              <span
                (click)="
                  question.secondLabel.click
                    ? question.secondLabel.click()
                    : undefined
                "
                [class.first-line]="firstLine"
                [class.ellipsis]="question.secondLabel.ellipsis"
                class="second-label"
              >
                @switch (question.secondLabel.type) {
                  @case ('internalLink') {
                    <a
                      tabindex="-1"
                      class="link"
                      [routerLink]="question.secondLabel.internalLink"
                    >
                      {{ question.secondLabel.label | translate }}
                    </a>
                  }

                  @case ('externalLink') {
                    <a
                      tabindex="-1"
                      class="link"
                      [href]="question.secondLabel.externalLink"
                      target="_blank"
                    >
                      {{ question.secondLabel.label | translate }}
                    </a>
                  }

                  @case ('label') {
                    <label>
                      {{ question.secondLabel.label | translate }}
                    </label>
                  }

                  @case ('button') {
                    <span class="button">
                      <myre-action-button
                        type="tertiary"
                        [label]="question.secondLabel.label"
                        [onlyIcon]="
                          question.secondLabel.icon &&
                          !question.secondLabel.label
                        "
                        [withDesktopIcon]="true"
                        [reverse]="true"
                        [iconName]="question.secondLabel.icon"
                        position="left"
                        color="blue"
                      />
                    </span>
                  }

                  @case ('template') {
                    <ng-container
                      [ngTemplateOutlet]="question.secondLabel.template"
                      [ngTemplateOutletContext]="{
                        $implicit: question,
                        formGroup: formGroup,
                        context: question.secondLabel.context,
                      }"
                    />
                  }
                }
              </span>
            }
          </div>

          @if (question.questionMarkTooltip) {
            <ng-container
              [ngTemplateOutlet]="questionMarkTooltipTemplate"
              [ngTemplateOutletContext]="{
                tooltip: question.questionMarkTooltip,
              }"
            />
          }
        </div>
      }

      @switch (question.elementType) {
        @case ('calendar') {
          @let calendar = _asCalendar(question);
          <myre-separator />
          <myre-calendar-picker
            [id]="prefix + calendar.key"
            [formControlName]="calendar.key"
            [readonly]="calendar.readonly"
            [multiSelect]="calendar.multi"
            [numberOfMonths]="calendar.numberOfMonths"
            [minDate]="calendar.minDate"
            [maxDate]="calendar.maxDate"
            [dateFilter]="calendar.dateFilter"
            [startAt]="calendar.startAt"
          />
        }

        @case ('textbox') {
          <span
            class="textbox-container"
            [ngClass]="{ autocomplete: _asTextbox(question).autocomplete }"
          >
            @let textbox = _asTextbox(question);
            @if (!textbox.autocomplete) {
              @switch (textbox.type) {
                @case ('textarea') {
                  <myre-textarea
                    [readonly]="textbox.readonly"
                    [maxHeight]="textbox.maxHeight"
                    [maxLength]="textbox.maxLength"
                    [autoHeight]="true"
                    [formControlName]="textbox.key"
                    [id]="prefix + textbox.key"
                    [rows]="textbox.rows"
                    [cols]="textbox.cols"
                    [error]="hasError()"
                    [warning]="hasWarning()"
                    [placeholder]="textbox.placeholder"
                    (onFocus)="textbox.onFocus()"
                    (onBlur)="textbox.onBlur()"
                  />
                }

                @default {
                  <myre-textbox
                    autocomplete="off"
                    [readonly]="textbox.readonly"
                    [min]="textbox.min"
                    [max]="textbox.max"
                    [id]="prefix + textbox.key"
                    [type]="textbox.type"
                    [formControlName]="textbox.key"
                    [error]="hasError()"
                    [warning]="hasWarning()"
                    [picto]="textbox.picto"
                    [mask]="textbox.mask"
                    [placeholder]="textbox.placeholder"
                    [tooltip]="textbox.tooltip"
                    (onFocus)="textbox.onFocus()"
                    [displayOnly]="_getDisplayOnly(textbox)"
                    [displayOnlyNullValue]="_getDisplayOnlyNullValue(textbox)"
                    [pipe]="_getPipe(textbox)"
                    [unitTag]="textbox.getUnitTag()"
                    [contentPosition]="textbox.contentPosition"
                    (onBlur)="textbox.onBlur()"
                  />
                }
              }

              @if (textbox.iconNameActionButton) {
                <myre-action-button
                  class="button button--margin-left"
                  [onlyIcon]="true"
                  [withDesktopIcon]="true"
                  squareIcon="medium"
                  [iconName]="textbox.iconNameActionButton"
                  type="tertiary"
                  color="blue"
                  (onClick)="
                    textbox.onClickActionButton(textbox, textbox.getValue())
                  "
                />
              }
            }
            @if (textbox.autocomplete) {
              <myre-dropdown
                type="autocomplete"
                [id]="prefix + textbox.key"
                [readonly]="textbox.readonly"
                [required]="textbox.required"
                [placeholder]="textbox.placeholder"
                [autocompleteKeepValue]="textbox.getAutocompleteValue()"
                [autocompleteKeep]="textbox.autocompleteKeep"
                [list]="textbox.results"
                [picto]="textbox.picto"
                [formControlName]="textbox.key"
                (onInput)="search({ query: $event })"
              >
                @if (textbox.itemTemplate) {
                  <ng-template
                    dropdownTemplate="item"
                    let-item
                    let-hovered="hovered"
                    let-select="select"
                  >
                    <ng-container
                      [ngTemplateOutlet]="textbox.itemTemplate"
                      [ngTemplateOutletContext]="{ value: item.value }"
                    />
                  </ng-template>
                }

                @if (textbox.headerTemplate) {
                  <ng-template dropdownTemplate="header">
                    <ng-container [ngTemplateOutlet]="textbox.headerTemplate" />
                  </ng-template>
                }

                @if (textbox.footerTemplate) {
                  <ng-template dropdownTemplate="footer">
                    <ng-container [ngTemplateOutlet]="textbox.footerTemplate" />
                  </ng-template>
                }
              </myre-dropdown>
            }
          </span>
        }

        @case ('alternativeInput') {
          @let altInput = _asAlternativeInput(question);
          <ng-container
            [ngTemplateOutlet]="alternativeInputTempl"
            [ngTemplateOutletContext]="{
              $implicit: altInput,
              id: prefix + altInput.key,
              formGroup: formGroup,
            }"
          />
        }

        @case ('periodPicker') {
          @let periodPicker = _asPeriodPicker(question);
          <myui-period-picker
            [id]="prefix + periodPicker.key"
            [types]="periodPicker.types"
            [options]="periodPicker.options"
            [readonly]="periodPicker.readonly"
            [formControlName]="periodPicker.key"
            [placeholder]="periodPicker.placeholder"
          />
        }

        @case ('dropdown') {
          @let dropdown = _asDropdown(question);
          <myre-dropdown
            [type]="dropdown.multi ? 'multiselect' : 'select'"
            [id]="prefix + dropdown.key"
            [list]="dropdown.options | async"
            [readonly]="dropdown.readonly"
            [required]="dropdown.required"
            [picto]="dropdown.picto"
            [error]="hasError()"
            [warning]="hasWarning()"
            [noScroll]="dropdown.noScroll"
            [trackBy]="dropdown.trackBy"
            [placeholder]="dropdown.placeholder"
            [selectAllLabel]="dropdown.selectAllLabel"
            [formControlName]="dropdown.key"
            [searchPlaceholder]="dropdown.searchLabel"
            [searchType]="_getDropdownSearchType(dropdown)"
          >
            @if (dropdown.itemTemplate) {
              <ng-template
                dropdownTemplate="item"
                let-item
                let-hovered="hovered"
                let-select="select"
              >
                <ng-container
                  [ngTemplateOutlet]="dropdown.itemTemplate"
                  [ngTemplateOutletContext]="{ value: item.value }"
                />
              </ng-template>
            }

            @if (dropdown.headerTemplate) {
              <ng-template dropdownTemplate="header">
                <ng-container [ngTemplateOutlet]="dropdown.headerTemplate" />
              </ng-template>
            }

            @if (dropdown.footerTemplate) {
              <ng-template dropdownTemplate="footer">
                <ng-container [ngTemplateOutlet]="dropdown.footerTemplate" />
              </ng-template>
            }
          </myre-dropdown>
        }

        @case ('multi') {
          @let multiSelect = _asMultiSelect(question);
          <myre-multi-select
            [id]="prefix + multiSelect.key"
            [formControlName]="multiSelect.key"
            [readonly]="multiSelect.readonly"
            [draggable]="multiSelect.draggable"
            (dragged)="multiSelect.dragged($event)"
            [items]="multiSelect.options | async"
          />
        }

        @case ('date') {
          @let date = _asDate(question);
          <ng-container
            [ngTemplateOutlet]="dateTemplate"
            [ngTemplateOutletContext]="{
              $implicit: date,
              formGroup: formGroup,
              prefix: prefix,
            }"
          />
        }

        @case ('checkbox') {
          @let checkbox = _asCheckbox(question);
          <myre-checkbox
            [id]="prefix + checkbox.key"
            [formControlName]="checkbox.key"
            [readonly]="checkbox.readonly"
          />
          <span class="checkbox-label" [class.ellipsis]="checkbox.ellipsis">
            <label
              #ref
              [for]="prefix + checkbox.key + '-input'"
              [class.first-line]="firstLine"
              [class.ellipsis]="checkbox.ellipsis"
              class="myre-form__label"
            >
              {{ checkbox.label | translate }}
            </label>
            @if (checkbox.click) {
              <span>
                <span>&nbsp;</span>
                <a class="link" (click)="checkbox.click($event)">{{
                  checkbox.link | translate
                }}</a>
              </span>
            }
            @if (checkbox.required) {
              <span>&nbsp;*</span>
            }
          </span>
          @if (checkbox.ellipsis) {
            <myre-tooltip
              [fromRefWidth]="true"
              [ref]="ref"
              [keepOnContentOver]="true"
              >{{ checkbox.label | translate }}
            </myre-tooltip>
          }

          @if (question.questionMarkTooltip) {
            <ng-container
              [ngTemplateOutlet]="questionMarkTooltipTemplate"
              [ngTemplateOutletContext]="{
                tooltip: question.questionMarkTooltip,
              }"
            />
          }
        }

        @case ('radioGroup') {
          @let radioGroup = _asRadioGroup(question);
          <myre-radio-group
            [id]="prefix + radioGroup.key"
            [justify]="radioGroup.justify"
            [box]="radioGroup.box"
            [formControlName]="radioGroup.key"
            [readonly]="radioGroup.readonly"
            [options]="radioGroup.options"
            [display]="radioGroup.display"
          />
        }

        @case ('color') {
          @let color = _asColor(question);
          <myre-colorpicker
            [id]="prefix + color.key"
            [formControlName]="color.key"
            [verticalDirection]="color.verticalDirection"
            [horizontalDirection]="color.horizontalDirection"
            [activeColors]="color.activeColors"
            [readonly]="color.readonly"
          />
        }

        @case ('upload') {
          @let upload = _asUpload(question);
          <myre-upload
            #fileUploader
            [accept]="upload.accept"
            [adaptText]="upload.adaptText"
            [authorization]="upload.authorization"
            [auto]="upload.auto"
            [avatarSize]="upload.avatarSize"
            [bodyFormatter]="upload.bodyFormatter"
            [clearOnEnd]="upload.clearOnEnd"
            [color]="upload.color"
            [design]="upload.design"
            [formControlName]="upload.key"
            [getPreviewUrl]="upload.getPreviewUrl"
            [iconName]="upload.iconName"
            [id]="prefix + upload.key"
            [includeContentTypeHeader]="upload.includeContentTypeHeader"
            [keepUploadingOnDestroy]="upload.keepUploadingOnDestroy"
            [label]="upload.label"
            [loading]="upload.loading"
            [method]="upload.method"
            [name]="upload.name"
            [onUploadCallback]="upload.onUpload"
            [preview]="upload.preview"
            [readonly]="upload.readonly"
            [reverse]="upload.reverse"
            [type]="upload.type"
            [uploadId]="upload.uploadId"
            [url]="upload.url"
            [withAvatar]="upload.withAvatar"
            (onUploadStart)="upload.onUploadStart($event)"
            (onBeforeSend)="upload.onBeforeSend($event)"
            (onUpload)="upload.uploaded($event)"
            (onSelect)="upload.onSelect($event)"
            (onError)="upload.onError($event)"
          />
        }

        @case ('group') {
          @let group = _asGroup(question);
          <div
            [formArrayName]="group.key"
            [class.element-border]="group.elementBorder"
          >
            @switch (group.display) {
              @case ('group') {
                @for (
                  item of group.controls;
                  track trackBy($index);
                  let i = $index;
                  let last = $last
                ) {
                  @if (group.showItem$(group, i) | async) {
                    <div
                      class="group-elements-container"
                      [formGroupName]="i"
                      [class.group-elements-toggle]="group.collapsable"
                      [class.myre-content-box]="group.collapsable"
                      [class.container-padding]="
                        !!group.elementsContainerPadding
                      "
                      [class.space-bottom]="
                        !group.notSpaced && !last && !group.getLabel(i + 1)
                      "
                      [class.no-margin-bottom]="
                        last && !group.addButtonDisplayConditions()
                      "
                    >
                      @if (group.collapsable) {
                        <myre-toggle
                          #toggle="myreToggle"
                          [on]="group.getCollapsableToggleValue(i)"
                          (toggle)="group.onToggleChange($event, i)"
                        >
                          <myre-toggle-button
                            [disabled]="!group.allowToggleSwitch"
                          >
                            <div class="toggle-tile">
                              @if (group.allowToggleSwitch) {
                                <div class="toggle-action">
                                  <i
                                    [class.open]="toggle.on"
                                    class="myre-icon-arrow_dropdown"
                                  ></i>
                                </div>
                              }

                              <div
                                class="header"
                                [class.disabled]="!group.allowToggleSwitch"
                              >
                                <ng-container
                                  [ngTemplateOutlet]="questionGroupLabel"
                                />
                              </div>

                              @if (group.getCollapseKpi(i); as kpi) {
                                <ng-container
                                  [ngTemplateOutlet]="collapseKpi"
                                  [ngTemplateOutletContext]="{
                                    $implicit: kpi,
                                    question: group,
                                  }"
                                />
                              }

                              @if (
                                group.getQuestionCollapseIconValue(i);
                                as iconType
                              ) {
                                <div class="collapse-icon">
                                  <i
                                    #ref
                                    class="myre-icon-{{
                                      iconType
                                    }} icon-color-{{
                                      group.getQuestionCollapseIconColor(i)
                                    }}"
                                  ></i>

                                  @if (
                                    group.getQuestionCollapseIconTooltip(i);
                                    as tooltip
                                  ) {
                                    <myre-tooltip
                                      [ref]="ref"
                                      [keepOnContentOver]="true"
                                      >{{ tooltip | translate }}
                                    </myre-tooltip>
                                  }
                                </div>
                              }
                            </div>
                          </myre-toggle-button>

                          <myre-toggle-on #myreToggle0n>
                            <myre-separator
                              [withHorizontalSpace]="true"
                              [withVerticalSpace]="false"
                            />

                            <ng-container
                              [ngTemplateOutlet]="questionGroupContent"
                              [ngTemplateOutletContext]="{
                                hide: !toggle.on && !myreToggle0n.show,
                              }"
                            />
                          </myre-toggle-on>
                        </myre-toggle>
                      }

                      @if (!group.collapsable) {
                        <ng-container [ngTemplateOutlet]="questionGroupLabel" />
                        <ng-container
                          [ngTemplateOutlet]="questionGroupContent"
                        />
                      }

                      <ng-template #questionGroupLabel>
                        @if (group.getLabel(i) || group.showIndex) {
                          <label
                            [class.no-margin-top]="!hasMarginTop(group, i)"
                            [class.ellipsis]="group.ellipsis"
                            [style.font-size.px]="group.groupTitleSize"
                            class="myre-form__label upper-labels {{
                              'position-' + group.headerContentPosition
                            }}"
                          >
                            <span>
                              @if (group.showIndex) {
                                <span>{{ i + 1 }}</span>
                              }

                              @if (group.getLabel(i) && group.showIndex) {
                                <span>-</span>
                              }

                              @if (group.getLabel(i)) {
                                <span>{{ group.getLabel(i) | translate }}</span>
                              }
                            </span>

                            @if (group.getMarkTooltip(i)) {
                              <div>
                                <myre-icon-tooltip
                                  [label]="group.getMarkTooltip(i)"
                                />
                              </div>
                            }
                          </label>
                        }
                      </ng-template>

                      <ng-template #questionGroupContent let-hide="hide">
                        @if (!hide) {
                          <div
                            [class.myre-form-group]="
                              group.groupItemBorder !== false
                            "
                            [class.no-border-bottom]="group.notSpaced && !last"
                            class="{{ 'padding-' + group.groupContentPadding }}"
                          >
                            @if (group.headerTemplate) {
                              <ng-container
                                [ngTemplateOutlet]="group.headerTemplate"
                                [ngTemplateOutletContext]="{
                                  $implicit: group,
                                  formGroup: formGroup,
                                  index: i,
                                }"
                              />
                            }

                            <myre-form
                              [elements]="group.elements[i]"
                              [formGroup]="item"
                              [fromGroup]="true"
                              [prefix]="prefix + group.key + '-' + i + '-'"
                              [destroyFormGroupOnComponentDestroy]="false"
                            />

                            @if (
                              group.hasCustomActionButton || group.canDelete
                            ) {
                              <div class="myre-form-group-action-buttons">
                                @if (
                                  group.hasCustomActionButton &&
                                  !(group.customActionLastItemOnly && !last)
                                ) {
                                  <myre-action-button
                                    (onClick)="
                                      group.customActionAddItem
                                        ? group.addNewItem()
                                        : group.callCustomActionCallback(i)
                                    "
                                    [type]="group.customActionType"
                                    [label]="group.customActionLabel"
                                    [onlyIcon]="
                                      group.customActionIcon &&
                                      !group.customActionLabel
                                    "
                                    [withDesktopIcon]="true"
                                    [reverse]="true"
                                    [iconName]="group.customActionIcon"
                                    position="left"
                                    color="blue"
                                  />
                                }

                                @if (
                                  !group.hasCustomActionButton ||
                                  (group.customActionLastItemOnly && !last)
                                ) {
                                  <div></div>
                                }

                                @if (
                                  group.canDelete &&
                                  (!group.canDeleteItem ||
                                    group.canDeleteItem(group, i))
                                ) {
                                  <myre-action-button
                                    (onClick)="
                                      group.removeItemRequest(i, group)
                                    "
                                    [disabled]="
                                      group.canDeleteItem &&
                                      !group.canDeleteItem(group, i)
                                    "
                                    [type]="group.deleteButtonType"
                                    position="right"
                                    color="red"
                                  >
                                    <span class="delete-item">
                                      {{ group.deleteLabelButton | translate }}
                                      <i class="myre-icon-delete"></i>
                                    </span>
                                  </myre-action-button>
                                }
                              </div>
                            }

                            @if (group.footerTemplate) {
                              <ng-container
                                [ngTemplateOutlet]="group.footerTemplate"
                                [ngTemplateOutletContext]="{
                                  $implicit: group,
                                  index: i,
                                  canSave: canSave(item),
                                  emit: group.handleEvent,
                                  deleteIndex: group.removeItemRequest.bind(
                                    group,
                                    i
                                  ),
                                }"
                              />
                            }
                          </div>
                        }
                      </ng-template>
                    </div>
                  }
                }

                @if (group.addButtonDisplayConditions()) {
                  <div
                    class="add-button-container"
                    [class.alone]="!group.controls.length"
                  >
                    <myre-action-button
                      (onClick)="group.addNewItem()"
                      [type]="group.labelButtonType || 'dashed'"
                      color="blue"
                      [adaptText]="true"
                    >
                      <span class="add-item">
                        {{ group.labelButton | translate }}
                        <i class="myre-icon-add"></i>
                      </span>
                    </myre-action-button>
                  </div>
                }
              }

              @case ('list') {
                @let emptyLabelAndControls =
                  group.emptyLabelButton && !group.controls.length;
                <div
                  class="list no-padding"
                  [class.myre-content-box]="group.spacedList !== 'none'"
                  [class.no-margin]="group.notPaddedList"
                  [class.no-border]="group.notBorderedList"
                  [class.list--flush-x]="group.flushHorizontalSpacing"
                  [class.list--spaced-large]="group.spacedList === 'large'"
                  [class.list--spaced-small]="group.spacedList === 'small'"
                >
                  <div
                    #overflowContainer
                    [class.overflow]="group.overflowX"
                    [class.m-display:none]="emptyLabelAndControls"
                  >
                    <div
                      #header
                      class="myre-table__header-container"
                      [class.m-display:none]="emptyLabelAndControls"
                      [fixedOnScroll]="group.fixedHeaderFooter ? 'top' : null"
                      [containerReference]="referenceForFixedPosition"
                      [widthShouldMatchContainer]="true"
                      (fixedStateChange)="_isHeaderFixed = $event.fixed"
                    >
                      @if (group.headerTemplate) {
                        <ng-container
                          [ngTemplateOutlet]="group.headerTemplate"
                          [ngTemplateOutletContext]="{
                            $implicit: group,
                            formGroup: formGroup,
                          }"
                        />
                      } @else {
                        @if (
                          getQuestionsForList(group.generateElements({}));
                          as dummyElements
                        ) {
                          @if (
                            hasLabel(dummyElements) && !emptyLabelAndControls
                          ) {
                            <div class="myre-table__header">
                              @if (group.showIndex) {
                                <span class="myre-table__item index"></span>
                              }

                              @if (group.hasCustomActionButton) {
                                <span
                                  class="myre-table__item custom-button"
                                ></span>
                              }

                              @for (
                                dummyElement of dummyElements;
                                track trackBy($index);
                                let i = $index
                              ) {
                                @if (dummyElement.visible) {
                                  <div
                                    class="myre-table__item myre-form__label"
                                    [style.width]="dummyElement.width"
                                  >
                                    <span
                                      ellipsis
                                      [ngClass]="
                                        group.columnsAlign[i] || 'left'
                                      "
                                    >
                                      {{ dummyElement.label | translate }}
                                      @if (dummyElement.required) {
                                        <ng-container>*</ng-container>
                                      }
                                    </span>

                                    @if (
                                      dummyElement.secondLabel;
                                      as secondLabel
                                    ) {
                                      <span
                                        ellipsis
                                        class="second-label first-line"
                                        [class.large]="
                                          secondLabel.type === 'button'
                                        "
                                      >
                                        @switch (secondLabel.type) {
                                          @case ('label') {
                                            <label>{{
                                              secondLabel.label | translate
                                            }}</label>
                                          }
                                          @case ('internalLink') {
                                            <myre-link
                                              [routerLink]="
                                                secondLabel.internalLink
                                              "
                                            >
                                              {{
                                                secondLabel.label | translate
                                              }}
                                            </myre-link>
                                          }
                                          @case ('externalLink') {
                                            <myre-link
                                              [externalLink]="
                                                secondLabel.externalLink
                                              "
                                            >
                                              {{
                                                secondLabel.label | translate
                                              }}
                                            </myre-link>
                                          }
                                          @case ('info') {
                                            <i
                                              [tooltip]="
                                                secondLabel.label | translate
                                              "
                                              class="myre-icon-info"
                                            ></i>
                                          }
                                          @case ('button') {
                                            <myre-action-button
                                              type="tertiary"
                                              color="grey"
                                              [label]="secondLabel.label"
                                              [onlyIcon]="
                                                secondLabel.icon &&
                                                !secondLabel.label
                                              "
                                              [iconName]="secondLabel.icon"
                                              [withDesktopIcon]="true"
                                              (onClick)="secondLabel.click()"
                                            />
                                          }
                                          @case ('template') {
                                            <ng-container
                                              [ngTemplateOutlet]="
                                                secondLabel.template
                                              "
                                              [ngTemplateOutletContext]="{
                                                $implicit: secondLabel,
                                                formGroup: formGroup,
                                                context: secondLabel.context,
                                              }"
                                            />
                                          }
                                        }
                                      </span>
                                    }
                                  </div>
                                }
                              }

                              @if (group.canDelete) {
                                <span class="myre-table__item delete"></span>
                              }
                            </div>
                          }
                        }
                      }
                    </div>

                    <div
                      #total
                      [fixedOnScroll]="
                        group.fixedHeaderFooter && group.totalValuesCallback()
                          ? 'top'
                          : null
                      "
                      [containerReference]="referenceForFixedPosition"
                      [widthShouldMatchContainer]="true"
                      [offset]="header.getBoundingClientRect().height"
                      (fixedStateChange)="_isTotalFixed = $event.fixed"
                    >
                      @if (group.totalValuesCallback(); as headerTotalvalues) {
                        <div class="myre-table__line total">
                          <ng-container
                            [ngTemplateOutlet]="leftPanel"
                            [ngTemplateOutletContext]="{
                              $implicit: group,
                              actionDisabled: true,
                              i: -1,
                              last: false,
                            }"
                          />

                          @if (
                            getQuestionsForList(group.generateElements({}));
                            as dummyElements
                          ) {
                            @for (
                              dummyElement of dummyElements;
                              track trackBy($index)
                            ) {
                              @if (dummyElement.visible) {
                                <div
                                  class="myre-table__item"
                                  [style.width]="dummyElement.width"
                                >
                                  @if (
                                    dummyElement.elementType === 'textbox' &&
                                    dummyElement.type === 'number'
                                  ) {
                                    @let dummyTextbox =
                                      _asTextbox(dummyElement);
                                    <myre-textbox
                                      [tooltip]="
                                        headerTotalvalues[dummyTextbox.key]
                                          ?.tooltipMessage ?? null
                                      "
                                      [readonly]="true"
                                      [ngModel]="
                                        headerTotalvalues[dummyTextbox.key]
                                          ?.value
                                      "
                                      [ngModelOptions]="{ standalone: true }"
                                      [type]="dummyTextbox.type"
                                      [picto]="dummyTextbox.picto"
                                      [mask]="dummyTextbox.mask"
                                      [pipe]="_getPipe(dummyTextbox)"
                                      [unitTag]="dummyTextbox.getUnitTag()"
                                      [contentPosition]="
                                        dummyTextbox.contentPosition
                                      "
                                    />
                                  } @else {
                                    <myre-textbox
                                      [readonly]="true"
                                      [ngModel]="'-'"
                                      [ngModelOptions]="{ standalone: true }"
                                    />
                                  }
                                </div>
                              }
                            }
                          }

                          @if (group.canDelete) {
                            <span class="myre-table__item delete"></span>
                          }
                        </div>
                      }
                    </div>

                    <div
                      #referenceForFixedPosition
                      class="grid {{
                        group.spacedListItems ? 'm-gap-r:2' : ''
                      }}"
                    >
                      @for (
                        item of group.controls;
                        track trackBy($index);
                        let i = $index;
                        let last = $last
                      ) {
                        @if (group.showItem$(group, i) | async) {
                          <div
                            [formGroupName]="i"
                            class="myre-table__line {{
                              group.spacedListItems ? 'm-gap-c:2' : ''
                            }}"
                            [class.has-cell-errors]="group.showListCellErrors"
                          >
                            <ng-container
                              [ngTemplateOutlet]="leftPanel"
                              [ngTemplateOutletContext]="{
                                $implicit: group,
                                item: item,
                                actionDisabled: false,
                                i: i,
                                last: last,
                              }"
                            />

                            @for (
                              subQuestion of getQuestionsForList(
                                group.elements[i]
                              );
                              track $index;
                              let j = $index
                            ) {
                              <div
                                class="myre-table__item"
                                [class.m-flex]="group.showListCellErrors"
                                [class.m-flex-column]="group.showListCellErrors"
                                [class.m-flex-items:stretch]="
                                  group.showListCellErrors
                                "
                                [class.m-gap:2]="group.showListCellErrors"
                                [style.width]="subQuestion.width"
                                [ngClass]="group.columnsAlign[j] || 'left'"
                              >
                                @switch (subQuestion.elementType) {
                                  @case ('template') {
                                    @let subTemplate =
                                      _asElementTemplate(subQuestion);
                                    <ng-container
                                      [ngTemplateOutlet]="subTemplate.template"
                                      [ngTemplateOutletContext]="{
                                        $implicit: subTemplate,
                                        formGroup: formGroup,
                                      }"
                                    />
                                  }

                                  @case ('text') {
                                    @let subText = _asElementText(subQuestion);
                                    <span>
                                      {{
                                        subText.text
                                          | translate: subText.textBindings
                                      }}
                                    </span>
                                  }

                                  @case ('link') {
                                    @let subLink = _asElementLink(subQuestion);
                                    <myre-click-or-link
                                      [link]="subLink.link"
                                      [queryParams]="subLink.queryParams"
                                      [externalLink]="subLink.externalLink"
                                      (onClick)="
                                        subLink.onClick && subLink.onClick()
                                      "
                                    >
                                      @if (subLink.onClick) {
                                        <a class="link">
                                          {{
                                            subLink.text
                                              | translate: subLink.textBindings
                                          }}
                                        </a>
                                      } @else {
                                        {{
                                          subLink.text
                                            | translate: subLink.textBindings
                                        }}
                                      }
                                    </myre-click-or-link>
                                  }

                                  @case ('textbox') {
                                    <span class="textbox-container">
                                      @let subTextbox = _asTextbox(subQuestion);
                                      <myre-textbox
                                        autocomplete="off"
                                        [readonly]="subTextbox.readonly"
                                        [min]="subTextbox.min"
                                        [max]="subTextbox.max"
                                        [id]="
                                          prefix +
                                          group.key +
                                          '-' +
                                          i +
                                          '-' +
                                          subTextbox.key
                                        "
                                        [type]="subTextbox.type"
                                        [formControlName]="subTextbox.key"
                                        [error]="hasError(subTextbox)"
                                        [warning]="hasWarning(subTextbox)"
                                        [picto]="subTextbox.picto"
                                        [mask]="subTextbox.mask"
                                        [placeholder]="subTextbox.placeholder"
                                        [displayOnly]="
                                          _getDisplayOnly(subTextbox)
                                        "
                                        [displayOnlyNullValue]="
                                          _getDisplayOnlyNullValue(subTextbox)
                                        "
                                        [pipe]="_getPipe(subTextbox)"
                                        [unitTag]="subTextbox.getUnitTag()"
                                        [contentPosition]="
                                          subTextbox.contentPosition
                                        "
                                        [tooltip]="subTextbox.tooltip"
                                      />

                                      @if (subTextbox.iconNameActionButton) {
                                        <myre-action-button
                                          class="button button--margin-left"
                                          [onlyIcon]="true"
                                          [withDesktopIcon]="true"
                                          [squareIcon]="'medium'"
                                          [iconName]="
                                            subTextbox.iconNameActionButton
                                          "
                                          type="tertiary"
                                          color="blue"
                                          (onClick)="
                                            subTextbox.onClickActionButton(
                                              subTextbox,
                                              subTextbox.getValue()
                                            )
                                          "
                                        />
                                      }
                                    </span>
                                  }

                                  @case ('alternativeInput') {
                                    @let subAltInput =
                                      _asAlternativeInput(subQuestion);
                                    <ng-container
                                      [ngTemplateOutlet]="alternativeInputTempl"
                                      [ngTemplateOutletContext]="{
                                        $implicit: subAltInput,
                                        id:
                                          prefix +
                                          group.key +
                                          '-' +
                                          i +
                                          '-' +
                                          subAltInput.key,
                                        formGroup: group.controls[i],
                                      }"
                                    />
                                  }

                                  @case ('periodPicker') {
                                    @let periodPicker =
                                      _asPeriodPicker(subQuestion);
                                    <myui-period-picker
                                      [id]="prefix + periodPicker.key"
                                      [types]="periodPicker.types"
                                      [options]="periodPicker.options"
                                      [readonly]="periodPicker.readonly"
                                      [formControlName]="periodPicker.key"
                                      [placeholder]="periodPicker.placeholder"
                                    />
                                  }

                                  @case ('dropdown') {
                                    @let subDropdown = _asDropdown(subQuestion);
                                    <myre-dropdown
                                      [type]="
                                        subDropdown.multi
                                          ? 'multiselect'
                                          : 'select'
                                      "
                                      [id]="
                                        prefix +
                                        group.key +
                                        '-' +
                                        i +
                                        '-' +
                                        subDropdown.key
                                      "
                                      [list]="subDropdown.options | async"
                                      [picto]="subDropdown.picto"
                                      [theme]="subDropdown.theme"
                                      [subTheme]="subDropdown.subTheme"
                                      [fixedHeight]="subDropdown.fixedHeight"
                                      [error]="hasError(subDropdown)"
                                      [warning]="hasWarning(subDropdown)"
                                      [noScroll]="subDropdown.noScroll"
                                      [trackBy]="subDropdown.trackBy"
                                      [required]="subDropdown.required"
                                      [readonly]="subDropdown.readonly"
                                      [placeholder]="subDropdown.placeholder"
                                      [formControlName]="subDropdown.key"
                                      [searchPlaceholder]="
                                        subDropdown.searchLabel
                                      "
                                      [searchType]="
                                        _getDropdownSearchType(subDropdown)
                                      "
                                    >
                                      @if (subDropdown.itemTemplate) {
                                        <ng-template
                                          dropdownTemplate="item"
                                          let-item
                                          let-hovered="hovered"
                                          let-select="select"
                                        >
                                          <ng-container
                                            [ngTemplateOutlet]="
                                              subDropdown.itemTemplate
                                            "
                                            [ngTemplateOutletContext]="{
                                              value: item.value,
                                            }"
                                          />
                                        </ng-template>
                                      }

                                      @if (subDropdown.headerTemplate) {
                                        <ng-template dropdownTemplate="header">
                                          <ng-container
                                            [ngTemplateOutlet]="
                                              subDropdown.headerTemplate
                                            "
                                          />
                                        </ng-template>
                                      }

                                      @if (subDropdown.footerTemplate) {
                                        <ng-template dropdownTemplate="footer">
                                          <ng-container
                                            [ngTemplateOutlet]="
                                              subDropdown.footerTemplate
                                            "
                                          />
                                        </ng-template>
                                      }
                                    </myre-dropdown>
                                  }

                                  @case ('date') {
                                    @let subDate = _asDate(subQuestion);
                                    <ng-container
                                      [ngTemplateOutlet]="dateTemplate"
                                      [ngTemplateOutletContext]="{
                                        $implicit: subDate,
                                        formGroup: item,
                                        prefix:
                                          prefix + group.key + '-' + i + '-',
                                      }"
                                    />
                                  }

                                  @case ('checkbox') {
                                    @let subCheckbox = _asCheckbox(subQuestion);
                                    <myre-checkbox
                                      #checkbox
                                      [id]="
                                        prefix +
                                        group.key +
                                        '-' +
                                        i +
                                        '-' +
                                        subCheckbox.key
                                      "
                                      [formControlName]="subCheckbox.key"
                                      [readonly]="subCheckbox.readonly"
                                    />
                                  }

                                  @case ('color') {
                                    @let subColor = _asColor(subQuestion);
                                    <myre-colorpicker
                                      [id]="
                                        prefix +
                                        group.key +
                                        '-' +
                                        i +
                                        '-' +
                                        subColor.key
                                      "
                                      [formControlName]="subColor.key"
                                      [verticalDirection]="
                                        subColor.verticalDirection
                                      "
                                      [horizontalDirection]="
                                        subColor.horizontalDirection
                                      "
                                      [activeColors]="subColor.activeColors"
                                    />
                                  }

                                  @case ('button') {
                                    @let subButton =
                                      _asElementButton(subQuestion);
                                    <myre-action-button
                                      (onClick)="subButton.onClick()"
                                      [buttonId]="prefix + subButton.key"
                                      [adaptText]="true"
                                      [label]="subButton.label"
                                      [iconName]="subButton.iconName"
                                      [withDesktopIcon]="
                                        subButton.withDesktopIcon
                                      "
                                      [onlyIcon]="subButton.onlyIcon"
                                      [reverse]="
                                        subButton.reverse ||
                                        subButton.reverseIcon
                                      "
                                      [disabled]="subButton.readonly"
                                      [type]="subButton.type"
                                      [color]="subButton.color"
                                    />
                                  }
                                }

                                @if (
                                  group.showListCellErrors &&
                                  hasError(subQuestion)
                                ) {
                                  @for (
                                    cellError of getMessagesByType(
                                      'errors',
                                      subQuestion.getFormControl()
                                    );
                                    track trackByError($index, cellError)
                                  ) {
                                    <span class="myre-form_error">
                                      {{
                                        cellError.key
                                          | translate: cellError.binding
                                      }}
                                    </span>
                                  }
                                }

                                @if (
                                  group.showListCellErrors &&
                                  hasWarning(subQuestion)
                                ) {
                                  @for (
                                    cellWarning of getMessagesByType(
                                      'warnings',
                                      subQuestion.getFormControl()
                                    );
                                    track trackByError($index, cellWarning)
                                  ) {
                                    <span class="myre-form_warning">
                                      {{
                                        cellWarning.key
                                          | translate: cellWarning.binding
                                      }}
                                    </span>
                                  }
                                }
                              </div>
                            }

                            @if (group.canDelete) {
                              <div class="myre-table__item delete">
                                <myre-action-button
                                  (onClick)="group.removeItemRequest(i, group)"
                                  [disabled]="
                                    group.canDeleteItem &&
                                    !group.canDeleteItem(group, i)
                                  "
                                  [withDesktopIcon]="true"
                                  [attr.title]="group.deleteLabelButton"
                                  [onlyIcon]="true"
                                  squareIcon="medium"
                                  [border]="group.spacedList !== 'none'"
                                  iconName="delete"
                                  type="tertiary"
                                  color="red"
                                />
                              </div>
                            }
                          </div>
                        }
                      }

                      <ng-template
                        #leftPanel
                        let-question
                        let-item="item"
                        let-actionDisabled="actionDisabled"
                        let-i="i"
                        let-last="last"
                      >
                        @if (
                          question.showIndex ||
                          (question.hasCustomActionButton &&
                            !(question.customActionLastItemOnly && !last))
                        ) {
                          <div
                            class="left-panel"
                            [class.left-panel--sticky]="
                              question.stickyLeftPanel
                            "
                          >
                            @if (question.showIndex) {
                              <span class="myre-table__item index">{{
                                i + 1
                              }}</span>
                            }

                            @if (
                              question.hasCustomActionButton &&
                              !(question.customActionLastItemOnly && !last)
                            ) {
                              <myre-action-button
                                class="myre-table__item"
                                color="blue"
                                [disabled]="actionDisabled"
                                [type]="question.customActionType"
                                [onlyIcon]="
                                  question.customActionIcon &&
                                  !question.customActionLabel
                                "
                                [iconName]="question.customActionIcon"
                                [withDesktopIcon]="question.customActionIcon"
                                [label]="question.customActionLabel"
                                (onClick)="
                                  question.customActionAddItem
                                    ? question.addNewItem()
                                    : question.callCustomActionCallback(i)
                                "
                              />
                            }
                          </div>
                        }
                      </ng-template>
                    </div>
                  </div>

                  @if (
                    group.displayTotalElements ||
                    group.addButtonDisplayConditions() ||
                    group.emptyLabelButton ||
                    group.listFooterText ||
                    group.overflowX ||
                    group.footerButtons.length
                  ) {
                    <div
                      [fixedOnScroll]="
                        group.fixedHeaderFooter ? 'bottom' : null
                      "
                      [containerReference]="referenceForFixedPosition"
                      class="myre-table__footer {{
                        group.spacedListItems && group.controls.length
                          ? 'm-margin-t:2'
                          : ''
                      }}"
                    >
                      <div [class.m-width:full]="emptyLabelAndControls">
                        @if (emptyLabelAndControls) {
                          <myre-button
                            class="myre-table__item m-flex-grow:1 m-width:full"
                            [theme]="_ButtonTheme.Dashed"
                            [label]="group.emptyLabelButton"
                            (onClick)="_addNewItem(group)"
                          />
                        } @else if (group.addButtonDisplayConditions()) {
                          <myre-button
                            class="myre-table__item"
                            icon="myre-icon-add"
                            [theme]="_ButtonTheme.Secondary"
                            [label]="group.labelButton"
                            [disabled]="group.isAddButtonDisabled()"
                            (onClick)="_addNewItem(group)"
                          />
                        }

                        @if (group.listFooterText && group.controls.length) {
                          <span class="myre-table__item m-text-nowrap">{{
                            group.listFooterText(group.controls.length)
                          }}</span>
                        }

                        @if (group.displayTotalElements) {
                          <span [ngPlural]="group.controls.length">
                            <ng-template ngPluralCase="=0">
                              {{
                                '_library.message.formList.totalNoElements'
                                  | translate
                              }}
                            </ng-template>

                            <ng-template ngPluralCase="=1">
                              {{
                                '_library.message.formList.totalOneElement'
                                  | translate
                              }}
                            </ng-template>

                            <ng-template ngPluralCase="other">
                              {{
                                '_library.message.formList.totalSeveralElements'
                                  | translate: { number: group.controls.length }
                              }}
                            </ng-template>
                          </span>
                        }
                      </div>

                      @if (group.overflowX || group.footerButtons.length) {
                        <div class="myre-table__footer__buttons">
                          @for (
                            button of group.footerButtons;
                            track button.label
                          ) {
                            <myre-button
                              [label]="button.label"
                              [icon]="button.icon"
                              [disabled]="button.disabled"
                              [id]="'btn-' + button.id"
                              [ariaLabel]="button.ariaLabel"
                              [theme]="button.theme || _ButtonTheme.Primary"
                              [subTheme]="
                                button.subTheme || _ButtonSubTheme.Blue
                              "
                              [height]="button.height || _ButtonHeight.Medium"
                              (onClick)="button.onClick($event)"
                            />
                          }

                          @if (group.overflowX) {
                            <myre-scroll-control-panel
                              [scrollContainer]="overflowContainer"
                              [slaveContainers]="[header, total]"
                              [refreshOnDataChange]="[
                                group.controls,
                                _isHeaderFixed,
                                _isTotalFixed,
                              ]"
                            />
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            }
          </div>
        }

        @case ('container') {
          @let container = _asContainer(question);
          <div [formArrayName]="container.key">
            <div
              class="group-elements-container"
              [formGroupName]="0"
              [class.container-toggle]="container.collapsable"
              [class.myre-content-box]="container.collapsable"
            >
              @if (container.collapsable) {
                <myre-toggle
                  #toggle="myreToggle"
                  [on]="container.collapsableToggleValue"
                  (toggle)="container.onToggleChange($event)"
                >
                  <myre-toggle-button [disabled]="!container.allowToggleSwitch">
                    <div class="toggle-tile">
                      @if (container.allowToggleSwitch) {
                        <div class="toggle-action">
                          <i
                            [class.open]="toggle.on"
                            class="myre-icon-arrow_dropdown"
                          ></i>
                        </div>
                      }

                      <div
                        class="header"
                        [class.disabled]="!container.allowToggleSwitch"
                        [class.stretch]="
                          isNil(container.getCollapseKpi()?.getValue())
                        "
                      >
                        <ng-container
                          [ngTemplateOutlet]="questionContainerLabel"
                        />
                      </div>

                      @if (container.getCollapseKpi(); as kpi) {
                        <ng-container
                          [ngTemplateOutlet]="collapseKpi"
                          [ngTemplateOutletContext]="{
                            $implicit: kpi,
                            question: container,
                          }"
                        />
                      }
                    </div>
                  </myre-toggle-button>

                  <myre-toggle-on #myreToggle0n>
                    <myre-separator
                      [withHorizontalSpace]="true"
                      [withVerticalSpace]="false"
                    />

                    <ng-container
                      [ngTemplateOutlet]="questionContainerContent"
                      [ngTemplateOutletContext]="{
                        hide: !toggle.on && !myreToggle0n.show,
                      }"
                    />
                  </myre-toggle-on>
                </myre-toggle>
              }

              @if (!container.collapsable) {
                <ng-container [ngTemplateOutlet]="questionContainerLabel" />
                <ng-container [ngTemplateOutlet]="questionContainerContent" />
              }

              <ng-template #questionContainerLabel>
                @if (container.getLabelValue(); as labelValue) {
                  <label
                    [class.no-margin-top]="!hasMarginTop(container, 0)"
                    [class.ellipsis]="container.ellipsis"
                    [class.has-next-items]="
                      container.getSubLabelValue() ||
                      container.getCollapseTagValue()
                    "
                    class="myre-form__label upper-labels {{
                      'position-' + container.headerContentPosition
                    }}"
                  >
                    <span>{{ labelValue | translate }}</span>
                  </label>
                }

                @if (container.getSubLabelValue(); as sublabelValue) {
                  <span
                    class="myre-form__collapse-sublabel body-class blue"
                    [class.no-collapse-tag]="!container.getCollapseTagValue()"
                  >
                    {{ sublabelValue | translate }}
                  </span>
                }

                @if (container.getCollapseTag(); as tag) {
                  @if (tag.getValue(); as tagValue) {
                    <div
                      class="collapse-tag-wrapper"
                      [class.no-collapse-sublabel]="
                        !container.getSubLabelValue()
                      "
                    >
                      <myre-tag
                        [text]="tagValue | translate"
                        [type]="tag.tagType"
                      />
                    </div>
                  }
                }

                @if (container.getCollapseCheckbox(); as checkbox) {
                  <div
                    class="collapse-checkbox-wrapper"
                    [formGroup]="container.controls[0]"
                  >
                    <myre-checkbox
                      [formControlName]="checkbox.key"
                      [readonly]="checkbox.readonly"
                      [indeterminate]="checkbox.indeterminate"
                    />
                  </div>
                }
              </ng-template>

              <ng-template #questionContainerContent let-hide="hide">
                @if (!hide) {
                  <div class="myre-form-group">
                    @if (container.headerTitle) {
                      <div class="header-title">
                        <span>
                          {{ container.headerTitle | translate }}
                        </span>
                      </div>
                    }

                    <myre-form
                      [elements]="container.elements"
                      [formGroup]="container.controls[0]"
                      [prefix]="prefix + container.key + '-'"
                      [destroyFormGroupOnComponentDestroy]="false"
                    />

                    @if (container.footerTemplate) {
                      <div>
                        <ng-container
                          [ngTemplateOutlet]="container.footerTemplate"
                          [ngTemplateOutletContext]="{
                            $implicit: container,
                            emit: container.handleEvent,
                          }"
                        />
                      </div>
                    }
                  </div>
                }
              </ng-template>
            </div>
          </div>
        }

        @case ('switch') {
          @let switchQ = _asSwitch(question);
          <myre-switch
            [id]="prefix + switchQ.key"
            [formControlName]="switchQ.key"
            [readonly]="switchQ.readonly"
            [checked]="switchQ.initialValue"
            [label]="switchQ.label"
            [questionMarkTooltip]="switchQ.questionMarkTooltip"
            [onLabel]="switchQ.onLabel"
            [offLabel]="switchQ.offLabel"
            [withBackground]="switchQ.withBackground"
          />
        }

        @case ('wysiwyg') {
          @let wysiwyg = _asWysiwyg(question);
          <myre-wysiwyg
            [id]="prefix + wysiwyg.key"
            [formControlName]="wysiwyg.key"
            [readonly]="wysiwyg.readonly"
            [required]="wysiwyg.required"
            [placeholder]="wysiwyg.placeholder"
            [height]="wysiwyg.height ?? 120"
          />
        }

        @case ('multilevel-toggle') {
          @let multilevelToggle = _asMultilevelToggle(question);
          <multilevel-toggle
            [border]="multilevelToggle.border"
            [spaceTop]="multilevelToggle.spaceTop"
            [withPaddingBottom]="multilevelToggle.withPaddingBottom"
            [formControlName]="multilevelToggle.key"
          />
        }

        @case ('slider') {
          @let slider = _asSlider(question);
          <myre-slider
            [formControlName]="slider.key"
            [min]="slider.min"
            [max]="slider.max"
            [step]="slider.step"
            [readonly]="slider.readonly"
            [pipes]="slider.pipes"
            [valuePipes]="slider.valuePipes"
            [label]="slider.label"
          />
        }
      }
    </span>

    @switch (question.elementType) {
      @case ('alternativeInput') {
        @let altInputErr = _asAlternativeInput(question);
        <ng-container
          [ngTemplateOutlet]="errorContainer"
          [ngTemplateOutletContext]="{
            question: altInputErr.selectedSecondaryElement,
            control: altInputErr.selectedSecondaryControl,
          }"
        />
      }

      @default {
        <ng-container
          [ngTemplateOutlet]="errorContainer"
          [ngTemplateOutletContext]="{ question, control }"
        />
      }
    }

    <ng-template #errorContainer let-question="question" let-control="control">
      <div class="ewi-container">
        @if (hasError(question)) {
          @for (
            error of getMessagesByType('errors', control);
            track trackByError($index, error)
          ) {
            <span class="myre-form_error">
              {{ error.key | translate: error.binding }}
            </span>
          }
        }

        @if (hasWarning(question)) {
          @for (
            warning of getMessagesByType('warnings', control);
            track trackByError($index, warning)
          ) {
            <span class="myre-form_warning">
              {{ warning.key | translate: warning.binding }}
            </span>
          }
        }

        @if (hasInfo(question)) {
          @for (
            item of getMessagesByType('info', control);
            track trackByError($index, item)
          ) {
            <span class="myre-form_info">
              {{ item.key | translate: item.binding }}
            </span>
          }
        }
      </div>
    </ng-template>
  </span>

  @if (question.actionButton) {
    <myre-button
      class="action-button"
      [icon]="question.actionButton.icon"
      [theme]="_ButtonTheme.Secondary"
      (onClick)="question.actionButton.onClick(question, question.getValue())"
    />
  }
</div>

<ng-template #questionMarkTooltipTemplate let-tooltip="tooltip">
  <div class="upper-tooltip">
    <myre-icon-tooltip [class.first-line]="firstLine" [label]="tooltip" />
  </div>
</ng-template>

<ng-template #collapseKpi let-kpi let-question="question">
  @if (!isNil(kpi.getValue())) {
    <div #ref class="kpi">
      {{ kpi.getValue() }}
    </div>

    @if (kpi.tooltip) {
      <myre-tooltip [ref]="ref" [position]="'right'" [keepOnContentOver]="true">
        @switch (kpi.tooltip.type) {
          @case ('text') {
            {{ kpi.tooltip.text | translate }}
          }

          @case ('template') {
            <ng-container
              [ngTemplateOutlet]="kpi.tooltip.template"
              [ngTemplateOutletContext]="{ $implicit: question }"
            />
          }
        }
      </myre-tooltip>
    }
  }
</ng-template>

<ng-template
  #alternativeInputTempl
  let-question
  let-id="id"
  let-formGroup="formGroup"
>
  @let altInput = _asAlternativeInput(question);
  <ng-container [formGroup]="formGroup">
    <myre-textbox
      [id]="id"
      [required]="altInput.selectedSecondaryElement?.required"
      [type]="altInput.selectedSecondaryElement?.type"
      [formControl]="altInput.selectedSecondaryControl"
      [min]="altInput.selectedSecondaryElement?.min"
      [max]="altInput.selectedSecondaryElement?.max"
      [error]="hasError(altInput.selectedSecondaryElement)"
      [warning]="hasWarning(altInput.selectedSecondaryElement)"
      [readonly]="altInput.selectedSecondaryElement?.readonly"
      [picto]="altInput.selectedSecondaryElement?.picto"
      [mask]="altInput.selectedSecondaryElement?.mask"
      [placeholder]="altInput.selectedSecondaryElement?.placeholder"
      [tooltip]="altInput.selectedSecondaryElement?.tooltip"
      (onFocus)="altInput.selectedSecondaryElement?.onFocus()"
      [displayOnly]="_getDisplayOnly(altInput.selectedSecondaryElement)"
      [displayOnlyNullValue]="
        _getDisplayOnlyNullValue(altInput.selectedSecondaryElement)
      "
      [pipe]="_getPipe(altInput.selectedSecondaryElement)"
      [unitTag]="altInput.selectedSecondaryElement?.getUnitTag()"
      [contentPosition]="altInput.selectedSecondaryElement?.contentPosition"
      (onBlur)="altInput.selectedSecondaryElement?.onBlur()"
    >
      <myre-tag
        [type]="altInput.tagType"
        [withEllipsis]="false"
        [hover]="!altInput.readonly"
      >
        <myre-dropdown
          [type]="altInput.multi ? 'multiselect' : 'select'"
          [list]="altInput.options | async"
          [readonly]="altInput.readonly"
          [required]="altInput.required"
          [picto]="altInput.picto"
          [noScroll]="altInput.noScroll"
          [trackBy]="altInput.trackBy"
          [placeholder]="altInput.placeholder"
          [selectAllLabel]="altInput.selectAllLabel"
          [formControlName]="altInput.key"
          [adaptWidth]="true"
          [searchPlaceholder]="altInput.searchLabel"
          [searchType]="_getDropdownSearchType(altInput)"
        >
          @if (altInput.itemTemplate) {
            <ng-template
              dropdownTemplate="item"
              let-item
              let-hovered="hovered"
              let-select="select"
            >
              <ng-container
                [ngTemplateOutlet]="altInput.itemTemplate"
                [ngTemplateOutletContext]="{ value: item.value }"
              />
            </ng-template>
          }

          @if (altInput.headerTemplate) {
            <ng-template dropdownTemplate="header">
              <ng-container [ngTemplateOutlet]="altInput.headerTemplate" />
            </ng-template>
          }

          @if (altInput.footerTemplate) {
            <ng-template dropdownTemplate="footer">
              <ng-container [ngTemplateOutlet]="altInput.footerTemplate" />
            </ng-template>
          }
        </myre-dropdown>
      </myre-tag>
    </myre-textbox>
  </ng-container>
</ng-template>
